import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Sparkles, CheckCircle, Circle, Loader2, 
  Copy, Printer, FileText, ChevronDown, ChevronUp,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

// Use relative URL in production, env var in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Report sections in order
const REPORT_SECTIONS = [
  { 
    id: 'header_history', 
    name: 'Header & Case History',
    description: 'Client information, background, developmental history'
  },
  { 
    id: 'attention_behaviour', 
    name: 'Attention & Behaviour',
    description: 'Observations during assessment sessions'
  },
  { 
    id: 'prelinguistic', 
    name: 'Prelinguistic Skills',
    description: 'Eye contact, joint attention, turn-taking, imitation'
  },
  { 
    id: 'receptive_language', 
    name: 'Receptive Language',
    description: 'Language comprehension and understanding'
  },
  { 
    id: 'expressive_language', 
    name: 'Expressive Language',
    description: 'Language output, GLP stage analysis'
  },
  { 
    id: 'social_emotional', 
    name: 'Social-Emotional Skills',
    description: 'Self-advocacy, emotional regulation, peer interactions'
  },
  { 
    id: 'speech_motor', 
    name: 'Speech & Oral Motor',
    description: 'Articulation and motor planning'
  },
  { 
    id: 'summary_recommendations', 
    name: 'Summary & Recommendations',
    description: 'Key findings and actionable recommendations'
  }
];

export default function ComprehensiveReportGenerator({ onLogout }) {
  const { childId, assessmentId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [sectionStatuses, setSectionStatuses] = useState({});
  const [sectionContents, setSectionContents] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [error, setError] = useState(null);
  const [finalReport, setFinalReport] = useState(null);
  const [retryingSection, setRetryingSection] = useState(null);
  const [selectedSections, setSelectedSections] = useState(
    REPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );
  const [showSectionSelection, setShowSectionSelection] = useState(true);

  useEffect(() => {
    fetchData();
  }, [childId, assessmentId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [childRes, assessmentRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers }),
        axios.get(`${API}/assessments/detail/${assessmentId}`, { headers })
      ]);
      
      setChild(childRes.data);
      setAssessment(assessmentRes.data);
      
      // Check if report already exists
      if (assessmentRes.data.comprehensive_sections) {
        setSectionContents(assessmentRes.data.comprehensive_sections);
        setSectionStatuses(
          Object.keys(assessmentRes.data.comprehensive_sections).reduce((acc, key) => {
            acc[key] = 'complete';
            return acc;
          }, {})
        );
      }
      if (assessmentRes.data.generated_report) {
        setFinalReport(assessmentRes.data.generated_report);
      }
    } catch (error) {
      toast.error('Failed to load data');
      setError('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const generateSection = async (sectionId, sectionIndex, retryCount = 0) => {
    const token = localStorage.getItem('token');
    const maxRetries = 3;
    
    setSectionStatuses(prev => ({ ...prev, [sectionId]: 'generating' }));
    
    try {
      // Refresh token before generation
      await refreshTokenIfNeeded();
      
      // Start generation (returns immediately with task_id)
      const startResponse = await axios.post(
        `${API}/assessments/generate-section`,
        {
          assessment_id: assessmentId,
          section_id: sectionId,
          previous_sections: sectionContents
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30s just to start the task
        }
      );
      
      if (startResponse.data.status === 'generating' && startResponse.data.task_id) {
        // Poll for completion
        const taskId = startResponse.data.task_id;
        const result = await pollForCompletion(taskId, sectionId);
        
        if (result.status === 'complete' && result.content) {
          setSectionContents(prev => ({ ...prev, [sectionId]: result.content }));
          setSectionStatuses(prev => ({ ...prev, [sectionId]: 'complete' }));
          setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
          toast.success(`${REPORT_SECTIONS.find(s => s.id === sectionId)?.name} completed!`);
          return true;
        } else if (result.status === 'error') {
          const errorMsg = result.message || 'Section generation failed';
          setSectionStatuses(prev => ({ ...prev, [sectionId]: 'error' }));
          toast.error(errorMsg, { duration: 8000 });
          return false;
        }
      } else if (startResponse.data.status === 'complete') {
        // Sometimes returns immediately if cached
        setSectionContents(prev => ({ ...prev, [sectionId]: startResponse.data.content }));
        setSectionStatuses(prev => ({ ...prev, [sectionId]: 'complete' }));
        setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
        toast.success(`${REPORT_SECTIONS.find(s => s.id === sectionId)?.name} completed!`);
        return true;
      } else if (startResponse.data.status === 'error') {
        const errorMsg = startResponse.data.message || 'Failed to start generation';
        setSectionStatuses(prev => ({ ...prev, [sectionId]: 'error' }));
        toast.error(errorMsg, { duration: 8000 });
        return false;
      }
      
      return false;
    } catch (error) {
      // Log detailed error info
      console.error(`=== REPORT GENERATION ERROR ===`);
      console.error(`Section: ${sectionId}`);
      console.error(`Error object:`, error);
      console.error(`Error message:`, error.message);
      console.error(`Error code:`, error.code);
      console.error(`Error name:`, error.name);
      console.error(`Response status:`, error.response?.status);
      console.error(`Response data:`, error.response?.data);
      console.error(`Request URL:`, error.config?.url);
      console.error(`=== END ERROR ===`);
      
      // Retry on network errors
      const isNetworkError = error.message?.includes('Network Error') || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
      
      if (isNetworkError && retryCount < maxRetries) {
        const delayMs = Math.pow(2, retryCount) * 3000;
        toast.info(`Connection issue, retrying in ${delayMs/1000}s...`, { duration: 2000 });
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return generateSection(sectionId, sectionIndex, retryCount + 1);
      }
      
      setSectionStatuses(prev => ({ ...prev, [sectionId]: 'error' }));
      
      // Build detailed error message
      let errorMsg = 'Failed to generate section.';
      let errorDetails = '';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.status === 500) {
        errorMsg = 'Server error. Please check the console for details.';
        errorDetails = `Status: 500, Response: ${JSON.stringify(error.response?.data || {}).slice(0, 200)}`;
      } else if (error.response?.status === 429) {
        errorMsg = 'Rate limited. Please wait a moment and try again.';
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMsg = 'Authentication error. Please refresh the page and try again.';
      } else if (isNetworkError) {
        errorMsg = `Network error after ${maxRetries} retries. Details: ${error.message}`;
      } else {
        errorMsg = `Error: ${error.message || 'Unknown error'}`;
        errorDetails = `Code: ${error.code}, Name: ${error.name}`;
      }
      
      // Show error with details in console
      if (errorDetails) {
        console.error('Error details:', errorDetails);
      }
      
      toast.error(errorMsg, { duration: 10000 });
      return false;
    }
  };

  const pollForCompletion = async (taskId, sectionId, attempts = 0) => {
    const maxAttempts = 60; // Poll for up to 2 minutes (60 * 2s)
    const pollInterval = 2000; // 2 seconds
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/assessments/generation-status/${taskId}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      
      if (response.data.status === 'complete' || response.data.status === 'error') {
        return response.data;
      }
      
      // Still generating, wait and poll again
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        return pollForCompletion(taskId, sectionId, attempts + 1);
      }
      
      return { status: 'error', message: 'Generation timed out. Please try again.' };
    } catch (error) {
      // On network error during polling, wait and retry
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval * 2));
        return pollForCompletion(taskId, sectionId, attempts + 1);
      }
      return { status: 'error', message: 'Lost connection while generating. Please retry.' };
    }
  };

  // Helper function to refresh token if needed
  const refreshTokenIfNeeded = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Decode token to check expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      
      // If token expires in less than 5 minutes, refresh it
      if (exp - now < 5 * 60 * 1000) {
        const response = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
  };

  const pollSectionStatus = async (sectionId) => {
    const token = localStorage.getItem('token');
    let attempts = 0;
    const maxAttempts = 30;
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      attempts++;
      
      try {
        const response = await axios.get(
          `${API}/assessments/section-status/${assessmentId}/${sectionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.status === 'complete') {
          setSectionContents(prev => ({ ...prev, [sectionId]: response.data.content }));
          setSectionStatuses(prev => ({ ...prev, [sectionId]: 'complete' }));
          setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
          return;
        } else if (response.data.status === 'error') {
          setSectionStatuses(prev => ({ ...prev, [sectionId]: 'error' }));
          return;
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }
    
    setSectionStatuses(prev => ({ ...prev, [sectionId]: 'error' }));
  };

  const startGeneration = async () => {
    setGenerating(true);
    setError(null);
    setShowSectionSelection(false);
    
    // Get only selected sections
    const sectionsToGenerate = REPORT_SECTIONS.filter(s => selectedSections[s.id]);
    
    if (sectionsToGenerate.length === 0) {
      toast.error('Please select at least one section to generate');
      setGenerating(false);
      setShowSectionSelection(true);
      return;
    }
    
    // Refresh token at start
    await refreshTokenIfNeeded();
    
    for (let i = 0; i < sectionsToGenerate.length; i++) {
      setCurrentSection(i);
      const section = sectionsToGenerate[i];
      
      // Skip if already complete
      if (sectionStatuses[section.id] === 'complete') {
        continue;
      }
      
      // generateSection now has built-in retry with exponential backoff
      const success = await generateSection(section.id, i);
      
      if (!success) {
        // Section failed after all retries - allow user to manually continue or retry
        setError(`Failed to generate "${section.name}". You can retry this section or continue with remaining sections.`);
        setGenerating(false);
        return;
      }
      
      // Small delay between sections to avoid overwhelming the AI service
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // All sections complete - compile final report
    await compileFinalReport();
    setGenerating(false);
  };

  const compileFinalReport = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/assessments/compile-report`,
        { assessment_id: assessmentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.report) {
        setFinalReport(response.data.report);
        toast.success('Comprehensive report completed!');
      }
    } catch (error) {
      console.error('Error compiling report:', error);
    }
  };

  const retrySection = async (sectionId) => {
    setRetryingSection(sectionId);
    setError(null); // Clear any previous error
    toast.info(`Retrying ${REPORT_SECTIONS.find(s => s.id === sectionId)?.name}...`);
    
    // Refresh token before retry
    await refreshTokenIfNeeded();
    
    const success = await generateSection(sectionId, REPORT_SECTIONS.findIndex(s => s.id === sectionId), 0);
    setRetryingSection(null);
    
    // If this was the last section and it succeeded, compile the report
    if (success) {
      const allComplete = REPORT_SECTIONS.every(s => 
        sectionStatuses[s.id] === 'complete' || s.id === sectionId
      );
      if (allComplete && !finalReport) {
        await compileFinalReport();
      }
    }
  };

  const continueGeneration = async () => {
    // Continue from where we left off, skipping completed sections
    setGenerating(true);
    setError(null);
    
    for (let i = 0; i < REPORT_SECTIONS.length; i++) {
      setCurrentSection(i);
      const section = REPORT_SECTIONS[i];
      
      // Skip if already complete
      if (sectionStatuses[section.id] === 'complete') {
        continue;
      }
      
      const success = await generateSection(section.id, i);
      
      if (!success) {
        setError(`Failed to generate "${section.name}". You can retry this section or continue.`);
        setGenerating(false);
        return;
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    await compileFinalReport();
    setGenerating(false);
  };

  const checkAIHealth = async () => {
    toast.info('Checking AI service status...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai/health`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000
      });
      
      if (response.data.status === 'ok') {
        toast.success('AI service is working! Try generating again.', { duration: 5000 });
        setError(null);
      } else {
        const message = response.data.message || 'AI service issue detected';
        toast.error(message, { duration: 10000 });
        setError(message);
      }
    } catch (error) {
      const errorMsg = 'Could not check AI service. Please try again.';
      toast.error(errorMsg);
      setError(errorMsg);
    }
  };

  const copyReport = () => {
    if (finalReport) {
      navigator.clipboard.writeText(finalReport);
      toast.success('Report copied to clipboard');
    }
  };

  const printReport = () => {
    if (!finalReport) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Comprehensive Assessment Report - ${child?.name}</title>
          <style>
            body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
            h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
            h2 { color: #2d3748; margin-top: 24px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
            h3 { color: #4a5568; margin-top: 16px; }
            p { margin: 8px 0; }
            table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
            th { background: #f7fafc; }
            .section { margin-bottom: 24px; }
            pre { white-space: pre-wrap; font-family: inherit; }
          </style>
        </head>
        <body>
          <pre>${finalReport}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const toggleSectionSelection = (sectionId) => {
    setSelectedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const selectAllSections = () => {
    setSelectedSections(REPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}));
  };

  const deselectAllSections = () => {
    setSelectedSections(REPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: false }), {}));
  };

  const selectedCount = Object.values(selectedSections).filter(Boolean).length;
  const completedCount = Object.values(sectionStatuses).filter(s => s === 'complete').length;
  const progress = (completedCount / REPORT_SECTIONS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/assessments/${childId}`)} 
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Comprehensive Report</h1>
              <p className="text-sm text-stone-500">{child?.name} - {assessment?.assessment_type}</p>
            </div>
          </div>
          {finalReport && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyReport} className="rounded-full">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={printReport} className="rounded-full">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Selection - shown before generation starts */}
        {showSectionSelection && completedCount === 0 && !generating && (
          <Card className="p-6 mb-6 bg-white rounded-2xl border border-stone-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-medium text-lg">Select Report Sections</h2>
                <p className="text-sm text-stone-500">Choose which sections to include ({selectedCount} selected)</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllSections} className="rounded-full text-xs">
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAllSections} className="rounded-full text-xs">
                  Deselect All
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {REPORT_SECTIONS.map((section) => (
                <div 
                  key={section.id}
                  onClick={() => toggleSectionSelection(section.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedSections[section.id] 
                      ? 'border-primary bg-primary/5' 
                      : 'border-stone-200 bg-stone-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedSections[section.id] 
                        ? 'border-primary bg-primary' 
                        : 'border-stone-300'
                    }`}>
                      {selectedSections[section.id] && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-sm text-primary-900">{section.name}</h3>
                      <p className="text-xs text-stone-500 mt-0.5">{section.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-stone-100">
              <p className="text-sm text-stone-500">
                Estimated AI cost: ~{selectedCount} credit{selectedCount !== 1 ? 's' : ''}
              </p>
              <Button 
                onClick={startGeneration} 
                disabled={selectedCount === 0}
                className="rounded-full"
                data-testid="start-generation"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate {selectedCount} Section{selectedCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </Card>
        )}

        {/* Progress Overview - shown during/after generation */}
        {(!showSectionSelection || completedCount > 0 || generating) && (
        <Card className="p-6 mb-6 bg-white rounded-2xl border border-stone-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-medium text-lg">Report Generation Progress</h2>
              <p className="text-sm text-stone-500">{completedCount} of {REPORT_SECTIONS.filter(s => selectedSections[s.id]).length} sections complete</p>
            </div>
            {!generating && completedCount < REPORT_SECTIONS.filter(s => selectedSections[s.id]).length && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={fetchData} 
                  className="rounded-full"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
                <Button onClick={startGeneration} className="rounded-full" data-testid="continue-generation">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Continue Generation
                </Button>
              </div>
            )}
            {generating && (
              <Badge variant="secondary" className="px-4 py-2">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </Badge>
            )}
          </div>
          <Progress value={progress} className="h-2" />
          {generating && (
            <p className="text-xs text-stone-500 mt-2">
              Currently generating: {REPORT_SECTIONS[currentSection]?.name}
            </p>
          )}
        </Card>
        )}

        {/* Error Alert */}
        {error && (
          <Card className="p-4 mb-6 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 mb-3">{error}</p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={continueGeneration}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    disabled={generating}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Continue Generation
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={checkAIHealth}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    Check AI Service
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setError(null)}
                    className="text-red-600"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Sections */}
        <div className="space-y-3">
          {REPORT_SECTIONS.map((section, index) => {
            const status = sectionStatuses[section.id];
            const content = sectionContents[section.id];
            const isExpanded = expandedSections[section.id];
            
            return (
              <Card 
                key={section.id}
                className={`bg-white rounded-xl border transition-all ${
                  status === 'complete' ? 'border-green-200' : 
                  status === 'generating' ? 'border-blue-200' :
                  status === 'error' ? 'border-red-200' :
                  'border-stone-100'
                }`}
              >
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => content && toggleSection(section.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-stone-100">
                      {status === 'complete' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : status === 'generating' ? (
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      ) : status === 'error' ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-stone-300" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{section.name}</p>
                      <p className="text-xs text-stone-500">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status === 'error' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); retrySection(section.id); }}
                        className="rounded-full text-red-600"
                        disabled={retryingSection === section.id}
                      >
                        {retryingSection === section.id ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3 mr-1" /> Retry
                          </>
                        )}
                      </Button>
                    )}
                    {content && (
                      isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />
                    )}
                  </div>
                </div>
                
                <AnimatePresence>
                  {isExpanded && content && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-0">
                        <div className="bg-stone-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                          <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans">{content}</pre>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>

        {/* Final Report */}
        {finalReport && (
          <Card className="mt-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-green-700" />
                <div>
                  <h3 className="font-medium text-green-900">Complete Report Ready</h3>
                  <p className="text-sm text-green-700">All sections generated and compiled</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyReport} className="rounded-full">
                  <Copy className="w-4 h-4 mr-1" /> Copy All
                </Button>
                <Button variant="outline" size="sm" onClick={printReport} className="rounded-full">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl max-h-96 overflow-y-auto border border-green-100">
              <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans">{finalReport}</pre>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
